import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SignIn } from '@clerk/clerk-react';
import toast from '../design-system/toast';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { AUTH_PROVIDER } from '../config/auth';
import LocalLoginForm from '../components/auth/LocalLoginForm';
import OIDCLoginButton from '../components/auth/OIDCLoginButton';

function LoginWidget() {
  const redirectUrl = sessionStorage.getItem('redirect_after_auth') || '/repositories';
  switch (AUTH_PROVIDER) {
    case 'local':
      return <LocalLoginForm />;
    case 'oidc':
      return <OIDCLoginButton />;
    case 'clerk':
    default:
      return <SignIn forceRedirectUrl={redirectUrl} signUpUrl='/sign-up' />;
  }
}

const HomePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { currentOrganization, isLoading: orgLoading } = useOrganization();

  // Redirect if already authenticated. Skip when a post-auth redirect is
  // stashed — AppContent consumes that key and navigates there.
  useEffect(() => {
    if (
      isAuthenticated &&
      !sessionStorage.getItem('redirect_after_auth') &&
      !orgLoading &&
      !currentOrganization?.is_demo
    ) {
      navigate('/repositories');
    }
  }, [currentOrganization?.is_demo, isAuthenticated, navigate, orgLoading]);

  // Handle OAuth error query parameters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const provider = params.get('provider');
    const error = params.get('error');

    if (error) {
      const providerName = provider === 'gitlab' ? 'GitLab' : provider === 'github' ? 'GitHub' : null;

      if (error === 'oauth_state_expired' || error === 'oauth_state_invalid') {
        toast.error('Connection expired. Please try again.');
      } else if (error === 'oauth_invalid_response') {
        toast.error(`${providerName || 'Provider'} returned an invalid response. Please try again.`);
      } else if (error === 'oauth_invalid_user_info') {
        toast.error(`Failed to retrieve ${providerName || 'provider'} user information. Please try again.`);
      } else if (error === 'oauth_connection_failed' && providerName) {
        toast.error(`Failed to connect ${providerName}. Please try again.`);
      } else {
        toast.error('Connection failed. Please try again.');
      }
      // Clean up URL
      window.history.replaceState({}, '', '/');
    }
  }, []);

  return <LoginWidget />;
};

export default HomePage;
